/**
 * Rareza — El botín, que es lo que convertía matar mobs en un bucle.
 *
 * FedeWorld ya tenía árbol de habilidades, hechizos, combos, armadura, arco,
 * jefes, encantamientos y mazmorras. Lo que no tenía era ningún motivo para
 * volver a entrar en una mazmorra: todas las espadas de hierro eran la misma
 * espada de hierro. Eso es lo que arregla esto.
 *
 * La idea la trae Minecraft Dungeons: el objeto no es solo su tipo, es su tipo
 * MÁS lo que le tocó al caer. Dos espadas de hierro pueden ser una basura y un
 * tesoro, y por eso merece la pena abrir el cofre.
 *
 * Este archivo es LIBRE DE DEPENDENCIAS, igual que ItemRegistry.js, para poder
 * importarse desde cualquier parte incluidos los tests.
 */

/* ── los cinco niveles ───────────────────────────────────────────────────
   Los pesos son de tirada: común sale ~57 veces por cada legendaria. Que la
   legendaria sea rara es lo que hace que valga la pena, así que este número no
   se toca a la ligera. */
export const RAREZAS = [
  { id: 'comun',      nombre: 'Común',      color: '#B8B8B8', peso: 1000, bonos: 0, mult: 1.00 },
  { id: 'buena',      nombre: 'Buena',      color: '#5AD65A', peso:  420, bonos: 1, mult: 1.15 },
  { id: 'rara',       nombre: 'Rara',       color: '#4A9EFF', peso:  150, bonos: 2, mult: 1.35 },
  { id: 'epica',      nombre: 'Épica',      color: '#C060F0', peso:   45, bonos: 3, mult: 1.65 },
  { id: 'legendaria', nombre: 'Legendaria', color: '#FFB020', peso:   10, bonos: 4, mult: 2.10 },
];

export const rarezaPorId = (id) => RAREZAS.find((r) => r.id === id) || RAREZAS[0];

/* ── los bonos que se pueden tirar ───────────────────────────────────────
   Cada uno tiene que poder APLICARSE de verdad por un sistema que ya existe.
   Un bono que no hace nada es una mentira bonita en una etiqueta. */
export const BONOS = [
  { id: 'dano',      nombre: 'Daño',      sufijo: '%', min:  5, max: 30, sistema: 'Combat' },
  { id: 'vida',      nombre: 'Vida',      sufijo: '%', min:  5, max: 25, sistema: 'Health' },
  { id: 'velocidad', nombre: 'Velocidad', sufijo: '%', min:  4, max: 18, sistema: 'Player' },
  { id: 'defensa',   nombre: 'Defensa',   sufijo: '%', min:  5, max: 28, sistema: 'ArmorSystem' },
  { id: 'mana',      nombre: 'Maná',      sufijo: '%', min:  8, max: 35, sistema: 'MagicSystem' },
  { id: 'suerte',    nombre: 'Suerte',    sufijo: '%', min:  5, max: 20, sistema: 'Rareza' },
];

export const bonoPorId = (id) => BONOS.find((b) => b.id === id) || null;

/** Azar inyectable: así los tests son deterministas y no hay que rezar. */
const azarPorDefecto = () => Math.random();

/**
 * Tira una rareza. `suerte` es un porcentaje que empuja hacia arriba: con
 * suerte 100 la legendaria sale el doble de veces.
 */
export function tirarRareza(suerte = 0, azar = azarPorDefecto) {
  const empuje = 1 + Math.max(0, suerte) / 100;
  /* La suerte se aplica solo a las rarezas altas: subirle el peso también a la
     común sería no hacer nada. */
  const pesos = RAREZAS.map((r, i) => (i === 0 ? r.peso : r.peso * empuje));
  const total = pesos.reduce((a, b) => a + b, 0);
  let t = azar() * total;
  for (let i = 0; i < RAREZAS.length; i++) {
    t -= pesos[i];
    if (t < 0) return RAREZAS[i];
  }
  return RAREZAS[0];
}

/**
 * Crea la parte «de instancia» de un objeto: su rareza y sus bonos tirados.
 * Se devuelve suelto para poder mezclarlo en el hueco del inventario, que hoy
 * es `{ blockId, count }` y así sigue siéndolo para todo lo que no mire esto.
 */
export function tirarObjeto({ suerte = 0, rareza = null, azar = azarPorDefecto } = {}) {
  const r = rareza ? rarezaPorId(rareza) : tirarRareza(suerte, azar);
  const disponibles = [...BONOS];
  const bonos = [];
  for (let i = 0; i < r.bonos && disponibles.length; i++) {
    /* Sin repetir: dos veces «Daño» en la misma espada se lee como un fallo. */
    const idx = Math.floor(azar() * disponibles.length);
    const b = disponibles.splice(idx, 1)[0];
    const valor = b.min + Math.floor(azar() * (b.max - b.min + 1));
    bonos.push({ id: b.id, valor });
  }
  return { rareza: r.id, bonos };
}

/** Cuánto suma un bono concreto en un objeto. 0 si no lo tiene. */
export function bonoDe(objeto, id) {
  if (!objeto || !objeto.bonos) return 0;
  const b = objeto.bonos.find((x) => x.id === id);
  return b ? b.valor : 0;
}

/** El multiplicador de la rareza; 1 para lo que no tenga rareza. */
export function multiplicadorDe(objeto) {
  return objeto && objeto.rareza ? rarezaPorId(objeto.rareza).mult : 1;
}

export function colorDe(objeto) {
  return objeto && objeto.rareza ? rarezaPorId(objeto.rareza).color : RAREZAS[0].color;
}

/** Texto para la etiqueta: «Rara · +18% Daño · +9% Maná». */
export function describir(objeto) {
  if (!objeto || !objeto.rareza) return '';
  const r = rarezaPorId(objeto.rareza);
  const partes = (objeto.bonos || []).map((b) => {
    const def = bonoPorId(b.id);
    return def ? `+${b.valor}${def.sufijo} ${def.nombre}` : null;
  }).filter(Boolean);
  return [r.nombre, ...partes].join(' · ');
}

/** Qué objetos pueden llevar rareza: armas, herramientas y armadura. Un
 *  bloque de tierra legendario no significa nada. */
export function admiteRareza(itemId, esArma) {
  if (esArma) return true;
  return typeof itemId === 'number' && itemId < 0;   // los negativos son objetos
}
